// CRA mirror — SSOT: modouls/_shared/frontend/widgets/client-errors/DeployUpdateScreen.jsx
// react-scripts does not transpile JSX from file: @markbase/modouls-shared-frontend in node_modules.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { isLikelyDeployError } from '@markbase/modouls-shared-frontend/widgets/client-errors/isLikelyDeployError.js';

const AUTO_RELOAD_SECONDS = 45;
const MAX_AUTO_RELOADS = 2;
const AUTO_RELOAD_COUNT_KEY = 'mb_deploy_auto_reload_count';

function readAutoReloadCount() {
  if (typeof window === 'undefined') return 0;
  try {
    const n = Number(window.sessionStorage.getItem(AUTO_RELOAD_COUNT_KEY) || '0');
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

function bumpAutoReloadCount() {
  if (typeof window === 'undefined') return MAX_AUTO_RELOADS;
  try {
    const next = readAutoReloadCount() + 1;
    window.sessionStorage.setItem(AUTO_RELOAD_COUNT_KEY, String(next));
    return next;
  } catch {
    return MAX_AUTO_RELOADS;
  }
}

export default function DeployUpdateScreen({ error, reset }) {
  const deployMode = useMemo(() => isLikelyDeployError(error), [error]);
  const [secondsLeft, setSecondsLeft] = useState(AUTO_RELOAD_SECONDS);
  const [autoReloadBlocked, setAutoReloadBlocked] = useState(() => readAutoReloadCount() >= MAX_AUTO_RELOADS);

  const reloadPage = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  }, []);

  const reloadPageFromAuto = useCallback(() => {
    if (typeof window === 'undefined') return;
    const count = bumpAutoReloadCount();
    if (count > MAX_AUTO_RELOADS) {
      setAutoReloadBlocked(true);
      return;
    }
    window.location.reload();
  }, []);

  const retryNow = useCallback(() => {
    if (reset) {
      reset();
      return;
    }
    reloadPage();
  }, [reset, reloadPage]);

  useEffect(() => {
    if (!deployMode || autoReloadBlocked) return undefined;

    const timer = window.setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          window.clearInterval(timer);
          reloadPageFromAuto();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [deployMode, autoReloadBlocked, reloadPageFromAuto]);

  const title = deployMode ? 'Обновляем платформу' : 'Что-то пошло не так';
  const lead = deployMode
    ? 'Приносим извинения за неудобства. Сейчас выполняется обновление — мы улучшаем работу сервиса для вас.'
    : 'Произошла ошибка при загрузке страницы. Попробуйте обновить экран или вернитесь чуть позже.';
  const note = deployMode
    ? autoReloadBlocked
      ? 'Автообновление остановлено после нескольких попыток, чтобы не зацикливать загрузку. Нажмите «Обновить страницу» вручную.'
      : 'Страница обновится автоматически. Если экран не исчезает — нажмите «Обновить страницу» или попробуйте снова через несколько минут.'
    : 'Если проблема повторяется, сообщите нам через поддержку — мы уже смотрим логи.';

  return (
    <main className="mb-deploy-update" role="alert" aria-live="polite" data-testid="mb-deploy-update-screen">
      <section className="mb-deploy-update__card">
        <div className="mb-deploy-update__badge">
          {deployMode ? 'Markbase · обновление' : 'Markbase · ошибка'}
        </div>

        <div className="mb-deploy-update__loader-wrap" aria-hidden="true">
          <div className="mb-deploy-update__loader-ring" />
          <div className="mb-deploy-update__loader-core">{deployMode ? '⚙' : '!'}</div>
        </div>

        <h1 className="mb-deploy-update__title">{title}</h1>
        <p className="mb-deploy-update__lead">{lead}</p>
        <p className="mb-deploy-update__note">{note}</p>

        {deployMode && !autoReloadBlocked ? (
          <p className="mb-deploy-update__countdown">
            Автообновление через <strong>{secondsLeft}</strong> сек.
          </p>
        ) : null}

        <div className="mb-deploy-update__actions">
          <button type="button" className="mb-deploy-update__btn mb-deploy-update__btn--primary" onClick={retryNow}>
            {deployMode ? 'Попробовать сейчас' : 'Повторить'}
          </button>
          <button type="button" className="mb-deploy-update__btn mb-deploy-update__btn--secondary" onClick={reloadPage}>
            Обновить страницу
          </button>
        </div>
      </section>
    </main>
  );
}
